/**
 * Per-attribute governance detail — same controls as Attribute Governance, focused on one attribute.
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Settings } from 'lucide-react';
import { PageHeader } from '../../../components/prioritization/PageHeader';
import { useAttributesStore } from '../../../stores/prioritization/attributesStore';
import {
  useAttributeGovernanceStore,
  type AttributeGovernanceTemplate,
} from '../../../stores/prioritization/attributeGovernanceStore';
import { AttributeGovernanceCard } from './AttributePermissions';

export default function AttributePermissionDetail() {
  const { attributeKey } = useParams<{ attributeKey: string }>();
  const navigate = useNavigate();

  const { attributes, fetchFromSupabase: fetchAttributes, syncState: attributesSyncState } = useAttributesStore();
  const {
    templates,
    fetchFromSupabase: fetchTemplates,
    createTemplate,
    updateTemplate,
    syncState: governanceSyncState,
  } = useAttributeGovernanceStore();

  const [notification, setNotification] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [savingAttribute, setSavingAttribute] = useState<string | null>(null);

  useEffect(() => {
    fetchAttributes();
    fetchTemplates();
  }, [fetchAttributes, fetchTemplates]);

  const attribute = useMemo(
    () => attributes.find((a) => a.key === attributeKey && !a.isCustomField),
    [attributes, attributeKey],
  );

  const templateMap = useMemo(() => {
    const map = new Map<string, AttributeGovernanceTemplate>();
    templates.forEach((t) => {
      if (t.scopeKind === 'global' && t.isActive) {
        map.set(t.attributeKey, t);
      }
    });
    return map;
  }, [templates]);

  const handleSaveTemplate = async (key: string, data: Partial<AttributeGovernanceTemplate>) => {
    setSavingAttribute(key);
    try {
      const existingTemplate = templateMap.get(key);

      if (existingTemplate) {
        await updateTemplate(existingTemplate.id, data);
      } else {
        await createTemplate({
          ...data,
          attributeKey: key,
          rangeDefinerRoles: data.rangeDefinerRoles || [],
          rangeApproverRoles: data.rangeApproverRoles || [],
          valueProposerRoles: data.valueProposerRoles || [],
          valueApproverRoles: data.valueApproverRoles || [],
          collectionMethod: data.collectionMethod || 'any_group_member',
          requiresEvidence: data.requiresEvidence || false,
          evidenceTypes: data.evidenceTypes || [],
          scopeKind: 'global',
          isActive: true,
          createdBy: 'admin',
        });
      }

      setNotification({
        type: 'success',
        message: `Governance settings saved for ${key}`,
      });
    } catch (err) {
      setNotification({
        type: 'error',
        message: `Failed to save: ${err instanceof Error ? err.message : 'Unknown error'}`,
      });
    } finally {
      setSavingAttribute(null);
      setTimeout(() => setNotification(null), 3000);
    }
  };

  const isLoading = attributesSyncState.isLoading || governanceSyncState.isLoading;

  if (!attributeKey) {
    return null;
  }

  return (
    <div className="space-y-6 max-w-4xl">
      <PageHeader
        icon={<Settings className="h-4 w-4 text-primary-600" />}
        iconBg="bg-primary-50"
        title="Attribute governance"
        subtitle="Who can define ranges and scales, propose values, approve them, and how values are collected for this attribute."
        actions={
          <button
            type="button"
            onClick={() => navigate('/prioritization/settings/attribute-permissions')}
            className="inline-flex items-center gap-2 text-sm text-[var(--text-sec)] hover:text-[var(--text-pri)]"
          >
            <ArrowLeft className="h-4 w-4" />
            All attributes
          </button>
        }
      />

      {notification && (
        <div
          className={`p-4  text-sm ${
            notification.type === 'success'
              ? 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)] border border-[var(--green)]'
              : 'bg-[var(--coral-dim)] text-[var(--coral)] border border-[var(--coral)]'
          }`}
        >
          {notification.message}
        </div>
      )}

      {isLoading ? (
        <div className="text-center py-12 text-[var(--text-ter)]">Loading…</div>
      ) : !attribute ? (
        <div className="card text-center py-10">
          <p className="text-[var(--text-sec)]">No scoring attribute found for “{attributeKey}”.</p>
          <button
            type="button"
            onClick={() => navigate('/prioritization/settings/attributes')}
            className="mt-4 btn btn-secondary"
          >
            Back to attributes
          </button>
        </div>
      ) : (
        <AttributeGovernanceCard
          attribute={attribute}
          template={templateMap.get(attribute.key)}
          onSave={(data) => handleSaveTemplate(attribute.key, data)}
          isSaving={savingAttribute === attribute.key}
          initialExpanded
        />
      )}
    </div>
  );
}
